// @react
import React, { useEffect } from 'react'
// @common
import { makeStyles } from '@material-ui/core/styles'
import colors from '@bit/atd.web.colors'
import Helper from 'utils/Helper.class'
import strings from 'lib/constants/strings'
// @graphql
import {
  LearningActivityOrderByList,
  WebinarsQuery,
  WebinarsQueryVariables,
} from 'lib/graphql/graphqlTypes'
import { useQuery } from '@apollo/client'
import { GET_WEBINARS } from 'graphql/queries/learning.query'
// @libraries
import Typography from '@material-ui/core/Typography'
import { ExternalRouteNames } from 'constants/routeNames'
import SortUtils from 'utils/sortutils'
// @components
import Box from '@bit/atd.web.box'
import Lozenge from '@bit/atd.web.lozenge'
import Table from '@bit/atd.web.table'
import Button from '@bit/atd.web.button'
import ButtonNav from '@bit/atd.web.button-nav'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
// @module components
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'
import useTable from 'modules/common/hooks/useTable'

/**
 * 4.0.1.4 MyATD - ActivityHistory - Learning - Webinars
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 * fields:
 * title
 * status
 * location
 * modality
 */

const useStyles = makeStyles({
  title: {
    display: 'block',
    fontWeight: 600,
    color: colors.black,
  },
  subtitle: {
    color: colors.gray500,
    fontWeight: 600,
  },
  status: {
    whiteSpace: 'nowrap',
  },
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  textAction: {
    marginTop: '24px',
    flexGrow: 1,
    display: 'flex',
    '& > a': {
      alignSelf: 'flex-end',
    },
  },
})

const Webinars = () => {
  /******************************************
   * HOOKS
   ******************************************/

  const {
    Pagination,
    paginationProps,
    ordering,
    inputVal,
    setRefetch,
    sortHandler,
  } = useTable<WebinarsQueryVariables>({
    orderBy: LearningActivityOrderByList.TITLE,
  })

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    WebinarsQuery,
    WebinarsQueryVariables
  >(GET_WEBINARS, {
    variables: {
      input: inputVal,
    },
  })

  useEffect(() => setRefetch(refetch), [setRefetch, refetch])
  const classes = useStyles()

  /******************************************
   * FUNCTIONS
   ******************************************/
  const webinars = data?.webinars?.data || []
  const meta = data?.webinars?.meta
  if (isError) {
    return <QueryError />
  }

  /**
   *
   */
  const rowActions = () => {
    if (!webinars) {
      return [[<div key={1} />]]
    }
    return webinars.map((item: any) => {
      const actions = []
      if (item.url) {
        actions.push(
          <ButtonNav
            key={1}
            variant="secondary"
            label="View"
            linkType="external"
            href={item.url}
          />,
        )
      }
      return actions
    })
  }

  const tableRows = () => {
    return webinars.map((item: any) => {
      const content = item
      return [
        <span key={1}>
          <strong className={classes.title}>{content.title}</strong>
        </span>,
        /*content.sessions[0]?.status || '' @todo status is missing STATIC*/
        <span key={2} className={classes.status}>
          <Lozenge variant="outline" aria-label="In-Progress">
            {Helper.getStatus(content.date)}
          </Lozenge>
        </span>,
        <span key={3}>
          <strong className={classes.title}>
            {Helper.globalDateFormatter(content.date)}
          </strong>
          {/*<strong className={classes.subtitle}>{content.description}</strong>*/}
        </span>,
      ]
    })
  }

  const renderContent = () => {
    if (data?.webinars?.data && data?.webinars?.data?.length > 0) {
      return (
        <PaginationScrollWrapper {...paginationProps}>
          <Table
            defaultSort={SortUtils.transformSortValues(ordering)}
            header={[
              {
                label: 'TITLE',
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: 'A to Z',
                  descLabel: 'A to Z',
                  showLabel: false,
                },
              },
              {
                label: 'STATUS',
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: 'A to Z',
                  descLabel: 'A to Z',
                  showLabel: false,
                },
              },
              {
                label: 'Details',
                mobileLayout: {
                  width: 'full',
                  showLabel: true,
                },
              },
            ]}
            rows={tableRows()}
            actions={{
              label: 'Actions',
              items: rowActions(),
            }}
          />
          <PaginationWrapper>
            <Pagination {...paginationProps} count={meta?.totalPages} />
          </PaginationWrapper>
        </PaginationScrollWrapper>
      )
    }
    return (
      <div>
        <Typography className={classes.textTitle} component={'h3'}>
          {strings.ACTIVITY_HISTORY.WEBINARS.NO_RESULTS_MESSAGE}
        </Typography>
        <Typography className={classes.textDescription} component={'p'}>
          {strings.ACTIVITY_HISTORY.WEBINARS.NO_RESULTS_DESCRIPTION}
        </Typography>
        <div className={classes.textAction}>
          <Button href={ExternalRouteNames.WEBCASTS_HOME}>See Webinars</Button>
        </div>
      </div>
    )
  }
  const formatCount = (count: any | null): string => {
    if (count) return `(${count})`
    return ''
  }

  return (
    <Box header={{ title: `Webinars ${formatCount(meta?.count)}` }}>
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </Box>
  )
}

export default Webinars
